import React, { useEffect, useMemo, useState } from "react";
import { UserInvites, Users } from "@app/shared/src/api/api.gen";
import { INewUserInvite, NewUserInvite, OmniUser } from "@app/shared/src/models";
import {
    Btn,
    Card,
    CardHead,
    FRow,
    FVal,
    Modal,
    Note,
    Select,
    Spinner,
    Switch,
    Table,
    TableBar,
    Tag,
    Td,
    TextInput,
    Th,
    formatAgo,
    useToast,
} from "@app/shared/src/components_v2";
import { useLoad } from "../../hooks/useLoad";
import { UrlCodec, urlOptionalId, useUrlState } from "../../hooks/useUrlState";
import { useOrg } from "../providers/OrgProvider";

/**
 * Who can reach this fleet, and who has been invited but hasn't accepted yet.
 *
 * Roles and per-facility permissions keep their full editing surface on the main dashboard — a
 * second partial editor would drift — but inviting someone is the thing a director actually needs
 * mid-incident ("get our new operations lead on this"), so that flow is first-class here, and any
 * row opens the person's detail without leaving the page.
 */
/** "all" is the unfiltered default, so it stays out of the URL. */
const ROLE_CODEC: UrlCodec<string> = {
    parse: (raw) => ({ value: raw || "all" }),
    format: (value) => urlOptionalId.format(value === "all" ? undefined : value),
};

export const TeamPage: React.FC = () => {
    const { org } = useOrg();
    const toast = useToast();
    const [inviteOpen, setInviteOpen] = useState(false);
    const [detail, setDetail] = useState<OmniUser>();
    // Free-form: roles come from the org, so the allowed set is not known at build time.
    const [role, setRole] = useUrlState<string>("role", "all", ROLE_CODEC);

    const team = useLoad<OmniUser[] | undefined>(() => (org?.id ? Users.getOrgUsers(org.id) : Promise.resolve([])), [org?.id]);
    const invites = useLoad(() => (org?.id ? UserInvites.getUserInvites(org.id) : Promise.resolve(undefined)), [org?.id]);

    const roles = useMemo(() => {
        const names = new Set<string>();
        for (const u of team.data ?? []) for (const r of u.roles ?? []) if (r.name) names.add(r.name);
        return Array.from(names).sort();
    }, [team.data]);

    const rows = useMemo(
        () => (team.data ?? []).filter((u) => role === "all" || (u.roles ?? []).some((r) => r.name === role)),
        [team.data, role],
    );

    const pending = (invites.data ?? []).filter((i) => !i.userExists);

    const resend = async (invite: (typeof pending)[number]) => {
        try {
            await UserInvites.resendInviteUser(invite);
            toast(`Invite resent to ${invite.email}`);
        } catch (err) {
            toast(err instanceof Error ? err.message : "Could not resend that invite");
        }
    };
    const remove = async (invite: (typeof pending)[number]) => {
        try {
            await UserInvites.deleteInviteUser(invite);
            toast("Invite withdrawn");
            invites.reload();
        } catch (err) {
            toast(err instanceof Error ? err.message : "Could not withdraw that invite");
        }
    };

    if (team.error) return <Note tone="crit">{team.error}</Note>;

    return (
        <div className="flex flex-col">
            <TableBar title="Team" count={team.data ? `${rows.length} of ${team.data.length}` : undefined}>
                <Select value={role} onChange={(e) => setRole(e.target.value)}>
                    <option value="all">All roles</option>
                    {roles.map((r) => (
                        <option key={r} value={r}>
                            {r}
                        </option>
                    ))}
                </Select>
                <Btn onClick={() => setInviteOpen(true)}>Invite</Btn>
            </TableBar>

            <Card>
                {team.isLoading ? (
                    <div className="grid place-items-center p-10 text-[var(--muted)]">
                        <Spinner />
                    </div>
                ) : rows.length === 0 ? (
                    <p className="px-5 py-6 text-[13px] text-[var(--muted)]">
                        {role === "all" ? "No users found for this organization." : "No one with that role."}
                    </p>
                ) : (
                    <Table>
                            <thead>
                                <tr>
                                    <Th>Name</Th>
                                    <Th>Email</Th>
                                    <Th>Roles</Th>
                                    <Th className="text-right" />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((u) => (
                                    <tr key={u.id} className="cursor-pointer hover:bg-[var(--surface-2)]" onClick={() => setDetail(u)}>
                                        <Td label="Name" className="font-semibold">{u.name?.trim() || "—"}</Td>
                                        <Td label="Email" className="text-[var(--muted)]">{u.email}</Td>
                                        <Td label="Roles">
                                            {u.roles && u.roles.length > 0 ? (
                                                <span className="flex flex-wrap gap-1.5">
                                                    {u.roles.slice(0, 3).map((r, i) => (
                                                        <Tag key={i} tone="mute">
                                                            {r.name?.trim() || "role"}
                                                        </Tag>
                                                    ))}
                                                </span>
                                            ) : (
                                                <span className="text-[var(--faint)]">—</span>
                                            )}
                                        </Td>
                                        <Td className="text-right">
                                            <Btn
                                                tone="quiet"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDetail(u);
                                                }}
                                            >
                                                Edit
                                            </Btn>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                    </Table>
                )}
            </Card>

            {pending.length > 0 && (
                <Card className="mt-4">
                    <CardHead title="Pending invites" note={`${pending.length}`} />
                    <div className="mt-2">
                        {pending.map((i) => (
                            <FRow key={i.id} title={i.name || i.email} sub={`${i.email} · sent ${i.dateSent ? formatAgo(i.dateSent) : "—"}`}>
                                <Btn tone="quiet" onClick={() => resend(i)}>
                                    Resend
                                </Btn>
                                <Btn tone="danger" onClick={() => remove(i)}>
                                    Withdraw
                                </Btn>
                            </FRow>
                        ))}
                    </div>
                </Card>
            )}

            <Note className="mt-4">
                Drivers don't appear here — they use cab cards, not accounts.
            </Note>

            <UserDetailModal user={detail} onClose={() => setDetail(undefined)} onSaved={() => team.reload()} />
            <InviteModal
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onDone={() => {
                    setInviteOpen(false);
                    invites.reload();
                }}
            />
        </div>
    );
};

/** Everything the console holds about one teammate, with their org roles editable in place. */
const UserDetailModal: React.FC<{ user?: OmniUser; onClose: () => void; onSaved: () => void }> = ({ user, onClose, onSaved }) => {
    const { org } = useOrg();
    const toast = useToast();
    const [selected, setSelected] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    // Reset the selection whenever a different person is opened.
    useEffect(() => {
        setSelected((user?.roles ?? []).map((r) => r.name).filter(Boolean) as string[]);
        setError(undefined);
    }, [user]);

    const dirty =
        JSON.stringify([...selected].sort()) !==
        JSON.stringify([...((user?.roles ?? []).map((r) => r.name).filter(Boolean) as string[])].sort());

    const save = async () => {
        if (!user?.id || !org?.id || busy) return;
        setBusy(true);
        setError(undefined);
        try {
            await Users.saveUserOrganizationRoles(user.id, org.id, selected);
            toast(`Roles updated for ${user.name ?? user.email}`);
            onSaved();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save those roles");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={Boolean(user)}
            onClose={onClose}
            title={user?.name ?? user?.email ?? ""}
            subtitle={user?.email}
            footer={
                <>
                    <Btn tone="quiet" onClick={onClose}>
                        Cancel
                    </Btn>
                    <Btn onClick={save} disabled={busy || !dirty}>
                        {busy ? "Saving…" : "Save roles"}
                    </Btn>
                </>
            }
        >
            {user && (
                <>
                    <div className="overflow-hidden rounded-[12px] border border-[var(--line)]">
                        <FRow title="Name">
                            <FVal mu>{user.name ?? "—"}</FVal>
                        </FRow>
                        <FRow title="Email">
                            <FVal mu>{user.email ?? "—"}</FVal>
                        </FRow>
                    </div>

                    <RoleSwitches label={`Roles in ${org?.name ?? "this organization"}`} selected={selected} onChange={setSelected} />

                    {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}

                    <p className="text-[12.5px] leading-relaxed text-[var(--muted)]">
                        These are organization-wide roles. Per-site permissions are still set on the main Omnisight dashboard.
                    </p>
                </>
            )}
        </Modal>
    );
};

const InviteModal: React.FC<{ open: boolean; onClose: () => void; onDone: () => void }> = ({ open, onClose, onDone }) => {
    const { org } = useOrg();
    const toast = useToast();
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [roles, setRoles] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();

    const canSend = Boolean(email.trim()) && roles.length > 0;

    const send = async () => {
        if (!org?.id || busy || !canSend) return;
        setBusy(true);
        setError(undefined);
        try {
            await UserInvites.inviteUser(
                new NewUserInvite({
                    organizationId: org.id,
                    email: email.trim(),
                    firstName: firstName.trim() || undefined,
                    lastName: lastName.trim() || undefined,
                    roles,
                    // Partial by intent: the server fills id, dates and the invite token.
                } as unknown as INewUserInvite),
            );
            toast(`Invite sent to ${email.trim()}`);
            setEmail("");
            setFirstName("");
            setLastName("");
            setRoles([]);
            onDone();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not send the invite");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Invite to this organization"
            subtitle="They get an email with an accept link and join with the roles you pick here"
            footer={
                <>
                    <Btn tone="quiet" onClick={onClose}>
                        Cancel
                    </Btn>
                    <Btn onClick={send} disabled={busy || !canSend}>
                        {busy ? "Sending…" : "Send invite"}
                    </Btn>
                </>
            }
        >
            <label className="text-[12px] text-[var(--muted)]">Email</label>
            <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            <div className="grid grid-cols-2 gap-3">
                <span>
                    <label className="text-[12px] text-[var(--muted)]">First name</label>
                    <TextInput value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1.5" />
                </span>
                <span>
                    <label className="text-[12px] text-[var(--muted)]">Last name</label>
                    <TextInput value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1.5" />
                </span>
            </div>
            <RoleSwitches label="Roles" selected={roles} onChange={setRoles} />
            {error && <p className="text-[12.5px] text-[var(--crit)]">{error}</p>}
        </Modal>
    );
};

const RoleSwitches: React.FC<{ label: string; selected: string[]; onChange: (roles: string[]) => void }> = ({
    label,
    selected,
    onChange,
}) => {
    const roleOptions = useLoad(() => Users.getAllRoles(), []);

    const toggle = (name: string) =>
        onChange(selected.includes(name) ? selected.filter((r) => r !== name) : [...selected, name]);

    return (
        <div>
            <label className="text-[12px] text-[var(--muted)]">{label}</label>
            {roleOptions.isLoading ? (
                <div className="grid place-items-center py-6 text-[var(--muted)]">
                    <Spinner />
                </div>
            ) : roleOptions.error ? (
                <Note tone="crit" className="mt-1.5">
                    Couldn't load the roles: {roleOptions.error}{" "}
                    <button type="button" onClick={() => void roleOptions.reload()} className="font-semibold underline">
                        Try again
                    </button>
                </Note>
            ) : (
                <div className="mt-1.5 overflow-hidden rounded-[12px] border border-[var(--line)]">
                    {(roleOptions.data ?? []).map((role) => {
                        const name = role.value ?? role.label ?? "";
                        const on = selected.includes(name);
                        return (
                            <button
                                key={name}
                                type="button"
                                role="switch"
                                aria-checked={on}
                                onClick={() => toggle(name)}
                                className="flex w-full items-center gap-3 border-t border-[var(--line)] px-4 py-3 text-left first:border-t-0 hover:bg-[var(--surface-2)]"
                            >
                                <span className="min-w-0 flex-1 text-[13.5px] font-medium">{role.label ?? name}</span>
                                <Switch on={on} />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
